import { Link } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';
import { usePageMeta } from '../lib/usePageMeta';

interface ChangelogEntry {
  date: string;
  headline: string;
  body: string;
}

const ENTRIES: ChangelogEntry[] = [
  {
    date: '2026-05-14',
    headline: 'Day-30 gate review template + snapshot CLI shipped',
    body: 'Phase 1 review can be run with a single command once the gate window closes (~2026-06-13). scripts/day-30-snapshot.ts pulls tenant/product/activity/cost numbers from Supabase. Decision tree codified; kill criteria from spec §11 explicit.',
  },
  {
    date: '2026-05-14',
    headline: 'Pilot onboarding playbook + onboard-pilot CLI shipped',
    body: 'Phase 1 launches with a manual-onboarding fallback for the first 3 hand-picked customers. scripts/onboard-pilot.ts provisions tenant + product + initial wrap + welcome login link in one command. scripts/list-tenants.ts gives a quick status table of all tenants.',
  },
  {
    date: '2026-05-14',
    headline: 'AI-CTO Track A intake worker deployed',
    body: 'Workers AI Vectorize provisioned with 768-dim BGE embeddings; KB ingest script ships with the worker. Anthropic Claude Haiku 4.5 wired for engine-recommendation reasoning.',
  },
  {
    date: '2026-05-14',
    headline: 'Nexlytics scorer engine adapter shipped',
    body: 'First engine to live behind the new @clarity-platform/engine-adapter interface. Multi-tenant ready; parser lift from the standalone Nexlytics codebase pending.',
  },
  {
    date: '2026-05-14',
    headline: 'Engine adapter interface package shipped',
    body: 'Engine<Wrap, Output, State> type, defineEngine validator, in-memory registry. Every future engine plugs into the same surface.',
  },
  {
    date: '2026-05-14',
    headline: 'Tenant provisioning service deployed',
    body: 'Atomic Postgres function provisions tenants + tenant_users + audit_trail row in a single transaction, gated behind a service-role shared secret.',
  },
  {
    date: '2026-05-14',
    headline: 'Stripe Connect onboarding wired',
    body: 'Customers connect their own Stripe accounts via Stripe Express; the platform never holds customer end-user funds.',
  },
  {
    date: '2026-05-14',
    headline: 'Stripe subscriptions live for the Pilot tier',
    body: '$149/mo Pilot tier checkout deployed end-to-end; webhook handler processes checkout.session.completed + customer.subscription.* events.',
  },
  {
    date: '2026-05-14',
    headline: 'Magic-link auth shipped',
    body: 'HMAC-SHA256 signed tokens with 15-min TTL → 30-day session cookies; emails delivered via Resend on the verified onwardsanalytics.com.au domain.',
  },
  {
    date: '2026-05-14',
    headline: 'Chassis package shipped',
    body: 'Tenant-scoped Supabase clients + RLS-aware context resolver landed in packages/chassis. Every Worker imports tenant context the same way.',
  },
  {
    date: '2026-05-14',
    headline: 'Initial Supabase schema applied',
    body: 'Tenants, tenant_users, engines catalogue, products, audit_trail, cost_events — all with RLS policies and SELECT-only audit policies.',
  },
  {
    date: '2026-05-14',
    headline: 'Monorepo bootstrapped',
    body: 'pnpm 9 + Turborepo + TypeScript 5.5 strict; workspace globs cover apps/* workers/* packages/* engines/*.',
  },
];

export function Changelog() {
  usePageMeta(
    'Changelog — Clarity Platform',
    'Every engine update, every chassis improvement, every new pattern shipped — dated and public. Clarity Platform change history.',
  );
  const intro = useReveal();
  const list = useReveal({ delay: 1 });
  const footer = useReveal();

  return (
    <main className="min-h-screen text-ink" style={{ background: 'var(--color-bg)' }}>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-28 md:py-36">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'var(--color-accent-soft)' }}
        />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p className="text-sm uppercase tracking-widest mb-5 font-medium" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>
            Transparency
          </p>
          <div ref={intro.ref} {...intro.props} className={intro.props.className}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight text-ink" style={{ fontFamily: 'var(--font-display)' }}>
              Changelog
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-normal" style={{ color: 'var(--color-mute)' }}>
              Every engine update, every chassis improvement, every new pattern shipped, dated.
            </p>
          </div>
        </div>
      </section>

      {/* ── Entry list ── */}
      <div className="max-w-4xl mx-auto px-6 pb-8">
        <div ref={list.ref} {...list.props} className={list.props.className}>
          <ol className="space-y-0">
            {ENTRIES.map((entry, idx) => (
              <li key={`${entry.date}-${idx}`}>
                <article className="py-10" style={{ borderBottom: '1px solid var(--color-hairline)' }}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-8">
                    <time
                      dateTime={entry.date}
                      className="flex-shrink-0 text-sm pt-1 w-28"
                      style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-mute)' }}
                    >
                      {entry.date}
                    </time>
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-ink leading-snug mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                        {entry.headline}
                      </h2>
                      <p className="leading-relaxed text-sm md:text-base" style={{ color: 'var(--color-mute)' }}>
                        {entry.body}
                      </p>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </div>

        {/* Footer nav */}
        <div className="py-16 mt-4" style={{ borderTop: '1px solid var(--color-hairline)' }}>
          <div ref={footer.ref} {...footer.props} className={`text-center ${footer.props.className}`}>
            <p className="mb-6" style={{ color: 'var(--color-mute)' }}>Want a deeper look at how this gets built?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {['/stack', '/playbook', '/engines'].map((route) => (
                <Link
                  key={route}
                  to={route}
                  className="inline-flex items-center justify-center px-6 py-3 rounded font-medium text-sm transition-colors"
                  style={{ border: '1px solid var(--color-hairline)', color: 'var(--color-ink)' }}
                >
                  {route}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
